import React from 'react';
import { styled } from '@linaria/react';
import { animated } from 'react-spring';
import { Star } from 'lucide-react';

import { normalize } from '@/utils';
import useBoop from '@/hooks/use-boop';

import VisuallyHidden from '@/components/VisuallyHidden';

const useAngledBoop = (index: number) => {
  let angle = index * (360 / 5) - 90;
  // The star icon we're using is _not_ a perfect star, so we need to cheat a bit.
  if (index === 1) {
    angle += 3.5;
  } else if (index === 4) {
    angle -= 3.5;
  }
  const angleInRads = (angle * Math.PI) / 180;
  const distance = 42;

  const x = distance * Math.cos(angleInRads);
  const y = distance * Math.sin(angleInRads);

  const timing =
    normalize(index, 0, 4, 450, 600) * (1 + index * 0.22);
  const friction = normalize(index, 0, 4, 15, 40);

  const boop = useBoop({
    x,
    y,
    timing,
    scale: 1.4,
    springConfig: { tension: 180, friction },
  });

  return boop;
};

const CircleDemo = () => {
  const [c1s, c1t] = useAngledBoop(0);
  const [c2s, c2t] = useAngledBoop(1);
  const [c3s, c3t] = useAngledBoop(2);
  const [c4s, c4t] = useAngledBoop(3);
  const [c5s, c5t] = useAngledBoop(4);
  const [starStyles, starTrigger] = useBoop({
    scale: 1.1,
    rotation: 10,
    timing: 150,
    springConfig: {
      tension: 300,
      friction: 6,
    },
  });

  const trigger = () => {
    c1t();
    c2t();
    c3t();
    c4t();
    c5t();
    starTrigger();
  };

  return (
    <Wrapper>
      <Button onMouseEnter={trigger} onClick={trigger}>
        <IconWrapper style={starStyles}>
          <Star size={48} />
        </IconWrapper>
        <VisuallyHidden>Star icon</VisuallyHidden>
      </Button>
      <Circle style={c1s} />
      <Circle style={c2s} />
      <Circle style={c3s} />
      <Circle style={c4s} />
      <Circle style={c5s} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: max-content;
`;

const Button = styled.button`
  position: relative;
  z-index: 3;
  padding: 8px;

  border-radius: 50%;
`;

const IconWrapper = styled(animated.span)`
  display: block;
  backface-visibility: hidden;

  i,
  svg {
    display: block !important;
  }

  svg {
    stroke: var(--color-text) !important;
    fill: var(--color-background) !important;
  }
`;

const Circle = styled(animated.div)`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 8px;
  height: 8px;
  margin: auto;
  border-radius: 50%;
  background: hsl(45deg, 100%, 50%);
  backface-visibility: hidden;
`;

export default CircleDemo;
